import React, { useMemo, useState } from 'react';
import {
  TabContent,
  TabPane,
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { Citation, RetrievedChunk, DocumentMetadata } from '../../../api/ai-supervisor';
import { MarkdownRenderer } from './MarkdownRenderer';

interface RetrievedChunksDisplayProps {
  retrievedChunks?: RetrievedChunk[];
  citations: Citation[];
  documentMetadata?: Record<string, DocumentMetadata>;
}

interface HighlightedChunk {
  chunk: RetrievedChunk;
  citations: Citation[];
  documentName: string;
}

export const RetrievedChunksDisplay = ({
  retrievedChunks,
  citations,
  documentMetadata,
}: RetrievedChunksDisplayProps) => {
  const [activeDocumentTab, setActiveDocumentTab] = useState<string>('');

  // Group chunks by document and include citation information
  const chunksByDocument = useMemo(() => {
    if (!retrievedChunks || retrievedChunks.length === 0) {
      return new Map<string, HighlightedChunk[]>();
    }

    const grouped = new Map<string, HighlightedChunk[]>();

    retrievedChunks.forEach((chunk) => {
      const docMetadata = documentMetadata?.[chunk.externalDocumentId];
      const documentName = docMetadata?.fileName || `Document ${chunk.externalDocumentId}`;

      // Find all citations that reference this chunk
      const chunkCitations = citations.filter(
        (cite) =>
          cite.sourceChunk.externalDocumentId === chunk.externalDocumentId &&
          cite.sourceChunk.chunkIndex === chunk.chunkIndex,
      );

      const highlightedChunk: HighlightedChunk = {
        chunk,
        citations: chunkCitations,
        documentName,
      };

      if (!grouped.has(chunk.externalDocumentId)) {
        grouped.set(chunk.externalDocumentId, []);
      }
      grouped.get(chunk.externalDocumentId)!.push(highlightedChunk);
    });

    // Sort chunks within each document by chunkIndex
    grouped.forEach((chunks, documentId) => {
      grouped.set(
        documentId,
        chunks.sort((a, b) => a.chunk.chunkIndex - b.chunk.chunkIndex),
      );
    });

    return grouped;
  }, [retrievedChunks, citations, documentMetadata]);

  // Set initial active tab when chunks load
  React.useEffect(() => {
    if (chunksByDocument.size > 0 && !activeDocumentTab) {
      const firstDocId = Array.from(chunksByDocument.keys())[0];
      setActiveDocumentTab(firstDocId);
    }
  }, [chunksByDocument, activeDocumentTab]);

  if (!retrievedChunks || retrievedChunks.length === 0) {
    return (
      <div className='text-muted text-center py-4'>
        <i className='fal fa-info-circle me-2' />
        No retrieved chunks available
      </div>
    );
  }

  const documents = Array.from(chunksByDocument.entries());
  const activeDocument = chunksByDocument.get(activeDocumentTab) ?? documents[0][1];

  return (
    <div>
      {/* Sticky Header - Stays visible while scrolling */}
      <div
        className='d-flex align-items-center justify-content-between border-top border-bottom background-ThemeBgToolbar p-3'
        style={{ position: 'sticky', top: 0, zIndex: 10 }}
      >
        <div className='d-flex align-items-center'>
          <div>
            <UncontrolledDropdown>
              <DropdownToggle caret size='lg' color='transparent' className='border mx-3 px-3 text-lg'>
                <i className='fal fa-file-alt me-2 text-ThemeAccentMedium text-xl' />
                {activeDocument[0].documentName || 'Unknown Document'}
              </DropdownToggle>
              <DropdownMenu>
                {documents.map(([documentId, chunks]) => {
                  const documentName = chunks[0]?.documentName || 'Unknown Document';
                  return (
                    <DropdownItem key={documentId} onClick={() => setActiveDocumentTab(documentId)}>
                      <i className='fal fa-file-alt me-2' />
                      {documentName}
                      <Badge color='secondary' className='ms-2'>
                        {chunks.length}
                      </Badge>
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </div>
        <div className='text-ThemeTextLight text-right d-flex flex-column gap-1'>
          {documents.length} document{documents.length !== 1 ? 's' : ''} used
          <div className='text-ThemeTextLight'>
            {activeDocument.length} chunk{activeDocument.length !== 1 ? 's' : ''} referenced
          </div>
          <div>
            <a
              href={activeDocument[0].chunk.externalDocumentId}
              onClick={(e) => {
                e.preventDefault(), alert('Not implemented yet');
              }}
              target='_blank'
              rel='noopener noreferrer'
            >
              <i className='fal fa-external-link-alt ms-2 text-ThemeAccentMedium' /> Access Document
            </a>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <TabContent
        activeTab={activeDocumentTab}
        className='p-4 border-top border-ThemeNeutralsDarkest background-ThemeBgDark'
      >
        {documents.map(([documentId, chunks]) => (
          <TabPane
            key={`${documentId}-tab`}
            tabId={documentId}
            className='bg-white p-6 border border-ThemeNeutralsDarkest'
            style={{
              boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 1) !important',
              overflowX: 'scroll',
              maxHeight: 'calc(100vh - 200px)',
            }}
          >
            <div className='px-6'>
              {chunks
                .sort((a, b) => a.chunk.chunkIndex - b.chunk.chunkIndex)
                .map(({ chunk, citations: chunkCitations }, i) => {
                  return (
                    <div key={`${documentId}-${chunk.chunkIndex}-${i}-chunk`} className='mb-4'>
                      {/* Chunk Header - styled like your mockup */}
                      {/* Content skipped indicator if needed */}
                      <div className='d-flex gap-2 align-items-center my-3 text-ThemeTextLight'>
                        <div className='flex-grow-1'>
                          <hr className='border-ThemeNeutralsDarkest' />
                        </div>
                        <div>
                          {chunk.pageStart !== undefined && chunk.pageStart > 0 && (
                            <Badge color='light' className='text-ThemeTextLight'>
                              Page {chunk.pageStart}
                            </Badge>
                          )}
                          <Badge color='light' className='text-ThemeTextLight ms-2'>
                            Chunk {chunk.chunkIndex}
                          </Badge>
                          <Badge color='secondary' className='ms-2'>
                            Relevance {Math.round(chunk.similarity * 100)}%
                          </Badge>
                          {chunkCitations.length > 0 && (
                            <Badge color='primary' className='ms-2'>
                              {chunkCitations.length} citation claim{chunkCitations.length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <div className='flex-grow-1'>
                          <hr className='border-ThemeNeutralsDarkest' />
                        </div>
                      </div>

                      {/* Chunk Text with Highlighting */}
                      <div className='citation'>
                        {chunkCitations.map((citation, ix) => (
                          <div
                            key={`citation-${citation.sourceChunk.externalDocumentId}-${citation.sourceChunk.chunkIndex}-${ix}-citation`}
                            className='answer-content border background-ThemeBgMedium rounded p-2 mb-3'
                          >
                            <h6>Claim #{ix + 1}</h6>
                            <MarkdownRenderer content={citation.claim} />
                          </div>
                        ))}
                      </div>

                      <div className='chunk-text'>
                        <MarkdownRenderer content={chunk.chunk} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </TabPane>
        ))}
      </TabContent>
    </div>
  );
};
